import { useEffect, useState, useCallback } from 'react';
import { pushAPI } from '../services/api';
import { getAuthToken } from '../services/auth';
import { Bell, Download, X, Share } from 'lucide-react';

const hasPushSupport = typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// ─── Sub-components ─────────────────────────────────────────────────

function IOSInstallBanner({ onDismiss }) {
  return (
    <div data-testid="ios-install-banner" className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:w-[360px] z-[9998] animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-gk-navy text-white rounded-xl shadow-2xl border border-gk-navy-light p-4">
        <div className="flex items-start gap-3">
          <img src="/icon-192.png" alt="GateKeeper" className="w-10 h-10 rounded-lg flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Install GateKeeper Pro</p>
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              Tap the <Share className="w-3.5 h-3.5 inline-block mx-0.5 text-gk-teal -mt-0.5" /> <span className="text-gk-teal font-medium">Share</span> button in Safari, then tap <span className="text-gk-teal font-medium">"Add to Home Screen"</span>
            </p>
          </div>
          <button onClick={onDismiss} className="text-slate-500 hover:text-slate-300 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function PWAInstallBanner({ onInstall, onDismiss }) {
  return (
    <div data-testid="pwa-install-banner" className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:w-[360px] z-[9998] animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-gk-navy text-white rounded-xl shadow-2xl border border-gk-navy-light p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gk-teal/20 flex items-center justify-center flex-shrink-0">
            <Download className="w-5 h-5 text-gk-teal" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Install GateKeeper Pro</p>
            <p className="text-xs text-slate-400 mt-0.5">Add to home screen for quick access & offline use</p>
          </div>
          <button onClick={onDismiss} className="text-slate-500 hover:text-slate-300 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <button data-testid="pwa-install-button" onClick={onInstall} className="flex-1 bg-gk-teal hover:bg-gk-teal-hover text-white font-semibold text-sm py-2 rounded-lg transition-colors">
            Install App
          </button>
          <button onClick={onDismiss} className="px-4 text-sm text-slate-400 hover:text-white transition-colors">
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

function PushPermissionBanner({ onEnable, onDismiss }) {
  return (
    <div data-testid="push-notification-banner" className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:w-[360px] z-[9998] animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-gk-navy text-white rounded-xl shadow-2xl border border-gk-navy-light p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gk-teal/20 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-gk-teal" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Enable Notifications</p>
            <p className="text-xs text-slate-400 mt-0.5">Get alerts for visitors, gate passes, bookings & more</p>
          </div>
          <button onClick={onDismiss} className="text-slate-500 hover:text-slate-300 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <button data-testid="enable-push-button" onClick={onEnable} className="flex-1 bg-gk-teal hover:bg-gk-teal-hover text-white font-semibold text-sm py-2 rounded-lg transition-colors">
            Enable
          </button>
          <button onClick={onDismiss} className="px-4 text-sm text-slate-400 hover:text-white transition-colors">
            Later
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isInStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
}

export function PushNotificationManager() {
  const [permission, setPermission] = useState(
    hasPushSupport ? Notification.permission : 'denied'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showIOSBanner, setShowIOSBanner] = useState(false);

  const token = getAuthToken();

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!sessionStorage.getItem('pwa-install-dismissed')) setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (!token) return;
    if (isIOS() && !isInStandaloneMode() && !sessionStorage.getItem('ios-install-dismissed')) {
      setShowIOSBanner(true);
    }
  }, [token]);

  useEffect(() => {
    if (!token || !hasPushSupport) return;
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then(async (sub) => {
        setIsSubscribed(!!sub);
        if (sub) {
          // Re-POST the existing browser subscription to the backend on every
          // mount. The backend uses `upsert: true`, so this is a no-op when
          // the row already exists but SELF-HEALS when the row was wiped
          // (410 stale-cleanup, DB re-seed, deploy migration, etc.). Without
          // this, the browser keeps thinking it's subscribed while the
          // backend has nothing to push to — the exact regression that made
          // visitor-approval popups stop appearing.
          try {
            const subJson = sub.toJSON();
            await pushAPI.subscribe({
              endpoint: subJson.endpoint,
              keys: subJson.keys,
              expiration_time: subJson.expirationTime || null,
            });
          } catch (err) {
            console.error('Push resubscribe (self-heal) failed:', err);
          }
        } else if (Notification.permission !== 'denied' && !sessionStorage.getItem('push-banner-dismissed')) {
          setShowBanner(true);
        }
      });
    });
  }, [token]);

  const subscribeToPush = useCallback(async () => {
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return;
      const registration = await navigator.serviceWorker.ready;
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const subJson = subscription.toJSON();
      await pushAPI.subscribe({ endpoint: subJson.endpoint, keys: subJson.keys, expiration_time: subJson.expirationTime || null });
      setIsSubscribed(true);
      setShowBanner(false);
    } catch (err) { console.error('Push subscription failed:', err); }
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShowInstallBanner(false);
    setDeferredPrompt(null);
  };

  const dismissPush = () => { setShowBanner(false); sessionStorage.setItem('push-banner-dismissed', 'true'); };
  const dismissInstall = () => { setShowInstallBanner(false); sessionStorage.setItem('pwa-install-dismissed', 'true'); };
  const dismissIOS = () => { setShowIOSBanner(false); sessionStorage.setItem('ios-install-dismissed', 'true'); };

  if (!token) return null;

  return (
    <>
      {showIOSBanner && <IOSInstallBanner onDismiss={dismissIOS} />}
      {showInstallBanner && deferredPrompt && <PWAInstallBanner onInstall={handleInstall} onDismiss={dismissInstall} />}
      {showBanner && !isSubscribed && permission !== 'denied' && <PushPermissionBanner onEnable={subscribeToPush} onDismiss={dismissPush} />}
    </>
  );
}
